import type { ReactNode } from 'react'

import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { directionOf, formatPercent, formatSigned } from '@/lib/market/format'

interface ChangeProps {
    value: number | null | undefined
    decimals?: number
    percent?: boolean
    className?: string
    showIcon?: boolean
}

const toneFor = (value: number | null | undefined): string => {
    const direction = directionOf(value)
    if (direction === 'up') return 'text-up'
    if (direction === 'down') return 'text-down'

    return 'text-muted-foreground'
}

export const DirectionIcon = ({ value, className }: { value: number | null | undefined, className?: string }): ReactNode => {
    const direction = directionOf(value)
    const Icon = direction === 'up' ? ArrowUpRight : direction === 'down' ? ArrowDownRight : Minus

    return <Icon aria-hidden='true' className={cn('size-3.5 shrink-0', className)} />
}

export const ChangeText = ({ value, decimals = 2, percent = true, className, showIcon = false }: ChangeProps): ReactNode => {
    const text = percent ? formatPercent(value, decimals) : formatSigned(value, decimals)

    return (
        <span className={cn('inline-flex items-center gap-0.5 tabular-nums', toneFor(value), className)}>
            {showIcon && <DirectionIcon value={value} />}
            {text}
        </span>
    )
}

export const ChangeBadge = ({ value, decimals = 2, className }: ChangeProps): ReactNode => {
    const direction = directionOf(value)

    return (
        <span
            className={cn(
                'inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-xs font-medium tabular-nums',
                direction === 'up' && 'bg-up/12 text-up',
                direction === 'down' && 'bg-down/12 text-down',
                direction === 'flat' && 'bg-secondary text-muted-foreground',
                className
            )}
        >
            <DirectionIcon value={value} className='size-3' />
            {formatPercent(value, decimals)}
        </span>
    )
}
